import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import { Broadcast } from '../../models/Broadcast';
import { BroadcastFilter } from '../../models/BroadcastFilter';
import { IntegrationAutomationsResponse, IntegrationSummary } from '../../models/IntegrationSource';
import { PaginatedResult } from '../model/paginated-result';

/**
 * "SMS via Integração" — sends originated by a third-party platform
 * (ActiveCampaign & co.) through the partner API.
 *
 * Three levels, because the underlying rows are one-SMS-per-contact and listing
 * them raw means thousands of lines:
 *
 *   listIntegrations() → one row per integration
 *   listAutomations()  → one row per automation, with its message template
 *   listSends()        → the individual messages of one automation
 *
 * Cada nível já devolve o nome de exibição da plataforma, então não há chamada
 * ao registro de `sources`: a tela nunca precisa traduzir um id por conta.
 */
@Injectable({ providedIn: 'root' })
export class IntegrationBroadcastsService {
  constructor(private http: HttpClient) { }

  /** Level 1 — one row per integration. */
  listIntegrations(filter?: BroadcastFilter): Observable<IntegrationSummary[]> {
    let url = `${environment.apiUrl}/integration-broadcasts?`;

    if (filter?.startDate) {
      url += `&startDate=${filter.startDate}`;
    }

    if (filter?.endDate) {
      url += `&endDate=${filter.endDate}`;
    }

    return this.http.get<IntegrationSummary[]>(url);
  }

  /** Level 2 — the automations of one integration. */
  listAutomations(source: string, filter?: BroadcastFilter): Observable<IntegrationAutomationsResponse> {
    let url = `${environment.apiUrl}/integration-broadcasts/${source}/automations?`;

    if (filter?.startDate) {
      url += `&startDate=${filter.startDate}`;
    }

    if (filter?.endDate) {
      url += `&endDate=${filter.endDate}`;
    }

    if (filter?.text) {
      url += `&text=${encodeURIComponent(filter.text)}`;
    }

    return this.http.get<IntegrationAutomationsResponse>(url);
  }

  /**
   * Level 3 — the individual messages.
   *
   * `automation` is sent even when empty: an empty value selects the sends that
   * arrived without an automation name, which is a real group, not "no filter".
   */
  listSends(
    source: string,
    automation: string | null,
    page: number = 1,
    perPage: number = 15,
    filter?: BroadcastFilter,
    phone?: string,
    status?: string
  ): Observable<PaginatedResult<Broadcast[]>> {
    let url = `${environment.apiUrl}/integration-broadcasts/${source}/sends?page=${page}&per_page=${perPage}`;

    // null means "every automation"; '' is a real selector for the sends that
    // arrived without one, so only null is omitted from the query.
    if (automation !== null) {
      url += `&automation=${encodeURIComponent(automation)}`;
    }

    if (filter?.startDate) {
      url += `&startDate=${filter.startDate}`;
    }

    if (filter?.endDate) {
      url += `&endDate=${filter.endDate}`;
    }

    if (filter?.text) {
      url += `&text=${encodeURIComponent(filter.text)}`;
    }

    if (phone) {
      url += `&phone=${encodeURIComponent(phone)}`;
    }

    if (status) {
      url += `&status=${status}`;
    }

    return this.http.get<PaginatedResult<Broadcast[]>>(url);
  }
}
